import type { Profile } from './api';
import type { AltiPosition, LatLng } from './types';
import type { Leg, Path } from './dataset';

/** Profile of a route walked from its `to` point: positions reversed, distances recomputed. */
export function reverseProfile(profile: Profile): Profile {
    if (profile.length === 0) return [];
    const reversed: Profile = [];
    const last = profile[profile.length - 1]!;
    reversed.push([last[0], last[1], last[2], 0]);
    let total = 0;
    for (let i = profile.length - 2; i >= 0; i--) {
        const p = profile[i]!;
        const next = profile[i + 1]!;
        total += next[3] - p[3];
        reversed.push([p[0], p[1], p[2], total]);
    }
    return reversed;
}

/** Elevation profiles keyed by route id, with the reversed variant cached. */
export class ProfileStore {
    private readonly forward = new Map<string, Profile>();
    private readonly backward = new Map<string, Profile>();

    constructor(profiles?: Record<string, Profile>) {
        if (profiles) for (const [id, profile] of Object.entries(profiles)) this.set(id, profile);
    }

    set(routeId: string, profile: Profile): void {
        this.forward.set(routeId, profile);
        this.backward.delete(routeId);
    }

    has(routeId: string): boolean {
        return this.forward.has(routeId);
    }

    get size(): number {
        return this.forward.size;
    }

    forLeg(leg: Leg): Profile | undefined {
        const profile = this.forward.get(leg.route.id);
        if (!profile) return undefined;
        if (leg.isForward) return profile;
        let reversed = this.backward.get(leg.route.id);
        if (!reversed) {
            reversed = reverseProfile(profile);
            this.backward.set(leg.route.id, reversed);
        }
        return reversed;
    }

    hasPath(path: Path): boolean {
        return path.legs.every((leg) => this.forward.has(leg.route.id));
    }
}

export interface ElevationSeries {
    code: string;
    routeId: string;
    color: string;
    /** [distance from the start of the path (m), altitude (m)] */
    points: [number, number][];
    /** Distance at the end of this leg, from the start of the path. */
    endDistance: number;
}

/** One series per leg, distances offset so that they chain. */
export function pathElevationSeries(path: Path, profiles: ProfileStore): ElevationSeries[] {
    const series: ElevationSeries[] = [];
    let offset = 0;
    for (const leg of path.legs) {
        const profile = profiles.forLeg(leg) ?? [];
        const points = profile.map((p): [number, number] => [p[3] + offset, p[2]]);
        const endDistance = points.length > 0 ? points[points.length - 1]![0] : offset;
        series.push({
            code: leg.code,
            routeId: leg.route.id,
            color: leg.color,
            points,
            endDistance,
        });
        offset = endDistance;
    }
    return series;
}

export function legLatLngs(leg: Leg, profiles: ProfileStore): LatLng[] {
    return (profiles.forLeg(leg) ?? []).map((p: AltiPosition): LatLng => [p[0], p[1]]);
}

export function pathLatLngs(path: Path, profiles: ProfileStore): LatLng[] {
    return path.legs.flatMap((leg) => legLatLngs(leg, profiles));
}
